import { z } from 'zod';

const twilioCapabilitiesSchema = z.record(z.string(), z.boolean()).default({});

export const twilioRegionSchema = z.enum(['us1', 'ie1']);
export type TwilioRegion = z.infer<typeof twilioRegionSchema>;

export const twilioPhoneNumberSchema = z.object({
  id: z.string().uuid(),
  twilio_sid: z.string().min(1),
  phone_number: z.string().min(1),
  friendly_name: z.string().nullable().optional(),
  capabilities: twilioCapabilitiesSchema,
  region: twilioRegionSchema.default('us1'),
  messaging_region: twilioRegionSchema.default('us1'),
  is_active: z.boolean(),
  last_synced_at: z.date().or(z.string().datetime()).nullable().optional(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
  inbound_configuration_id: z.string().uuid().nullable().optional(),
  inbound_configuration_name: z.string().nullable().optional(),
});

export type TwilioPhoneNumber = z.infer<typeof twilioPhoneNumberSchema>;

export const twilioPhoneNumberListSchema = z.array(twilioPhoneNumberSchema);

export const twilioNumberSyncResponseSchema = z.object({
  count: z.number().int().min(0),
  synced_at: z.date().or(z.string().datetime()),
});

export type TwilioNumberSyncResponse = z.infer<typeof twilioNumberSyncResponseSchema>;

export const twilioAvailablePhoneNumberSchema = z.object({
  phone_number: z.string().min(1),
  locality: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  country: z.string().length(2),
  capabilities: twilioCapabilitiesSchema,
});

export type TwilioAvailablePhoneNumber = z.infer<typeof twilioAvailablePhoneNumberSchema>;

export const twilioAvailablePhoneNumberListSchema = z.array(twilioAvailablePhoneNumberSchema);

/** Twilio number types we search and buy. Regulated countries need a bundle per type. */
export const twilioNumberTypeSchema = z.enum(['local', 'mobile']);
export type TwilioNumberType = z.infer<typeof twilioNumberTypeSchema>;

export const twilioAvailableNumbersQuerySchema = z.object({
  number_type: twilioNumberTypeSchema.default('local'),
  country_code: z
    .string()
    .trim()
    .length(2)
    .transform((value) => value.toUpperCase())
    .default('GB'),
  area_code: z.string().trim().min(1).max(10).optional(),
  contains: z
    .string()
    .trim()
    .min(1)
    .max(15)
    .optional()
    .refine(
      (value) => !value || value.replace(/[%*]/g, '').length >= 2,
      'Contains must include at least 2 digits/letters (for example: 55)',
    ),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type TwilioAvailableNumbersQuery = z.infer<typeof twilioAvailableNumbersQuerySchema>;

export const twilioPurchasePhoneNumberSchema = z.object({
  phone_number: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{6,14}$/, 'Must be a valid E.164 phone number (e.g., +441234567890)'),
  configuration_id: z.string().uuid().nullable().optional(),
  /** Which kind of number was searched; picks the matching regulatory bundle. */
  number_type: twilioNumberTypeSchema.optional(),
  /** ISO-2 country of the number, from the search results. Inferred from the prefix when absent. */
  country_code: z
    .string()
    .trim()
    .length(2)
    .transform((value) => value.toUpperCase())
    .optional(),
});

export type TwilioPurchasePhoneNumberDto = z.infer<typeof twilioPurchasePhoneNumberSchema>;

export const twilioAssignInboundNumberSchema = z.object({
  configuration_id: z.string().uuid().nullable(),
});

export type TwilioAssignInboundNumberDto = z.infer<typeof twilioAssignInboundNumberSchema>;

export const twilioUpdateRegionSchema = z
  .object({
    region: twilioRegionSchema.optional(),
    messaging_region: twilioRegionSchema.optional(),
  })
  .refine((data) => data.region !== undefined || data.messaging_region !== undefined, {
    message: 'At least one of region or messaging_region must be provided',
  });

export type TwilioUpdateRegionDto = z.infer<typeof twilioUpdateRegionSchema>;
